import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  formatName,
  getCurrentStatus,
  useRedirectToRevision,
} from "../../../util/AppUtils.jsx";
import CreateNewRevisionModal, {
  MODAL_MODES,
} from "../../../common/Modals/UnifiedRevisionModal";
import RevisionSelection from "./Modals/RevisionSelection";
import Snackbar from "../../../common/Snackbar";
import { parseRevision, revisionSort } from "../Utils/utils.js";

const RevisionTable = forwardRef(
  (
    {
      revisionData,
      revisionId,
      showCreateButton = false,
      isCreateButtonDisabled = false, // New prop to disable the create button
      canCreate = false, // Add canCreate prop for tooltip logic
      isReadOnly = false, // New prop to disable navigation and highlighting
      hideLinkStyle = false, // When true, do not show blue/link style on revision cells (e.g. from audit trail)
      disableRowNavigation = false, // When true, Rev column is not clickable (in addition to isReadOnly)
      // Required props for CreateRevisionModal
      partId,
      partNumber,
      type,
      description,
      myRevision,
      users = [],
      approvedUsers = [],
      releasedUsers = [],
      rule = null,
      onRevisionCreated = null,
      onCloseAdditionalInfo = null, // Callback to close additional information container
      showHeading = true,
      headingRightContent = null,
      tableMarginTop = null,
      tableMarginBottom = null,
    },
    ref,
  ) => {
    // Modal state
    const [isCreateRevisionModalOpen, setIsCreateRevisionModalOpen] =
      useState(false);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      type: "",
    });

    const showSnackbar = (message, type) => {
      setSnackbar({ open: true, message, type });
    };

    const handleCloseSnackbar = () => {
      setSnackbar({ open: false, message: "", type: "" });
    };
    const navigate = useNavigate();

    // Expose the onCreateRevision function to parent components
    useImperativeHandle(ref, () => ({
      onCreateRevision,
    }));

    // Comprehensive function to check revision creation conditions (from ReleaseRevision.js)
    const canCreateRevision = (currentRevision, allRevisions) => {
      if (!currentRevision || !allRevisions || allRevisions.length === 0) {
        return { canCreate: false, reason: "Invalid revision data" };
      }

      const currentStatus = currentRevision?.status_changes
        ?.find((sc) => sc.is_latest)
        ?.status_changed_to?.toLowerCase();

      const currentRevParsed = parseRevision(currentRevision?.rev);

      if (!currentRevParsed) {
        return { canCreate: false, reason: "Invalid current revision format" };
      }

      // Check for invalid revision types
      if (
        currentRevParsed.type === "special" ||
        currentRevParsed.type === "special_chars"
      ) {
        return {
          canCreate: false,
          reason:
            "This revision is invalid and cannot be used to create a new revision",
        };
      }

      // Sort all revisions to understand the hierarchy
      const sortedRevisions = [...allRevisions].sort((a, b) => {
        const revA = parseRevision(a.rev);
        const revB = parseRevision(b.rev);
        if (!revA && !revB) return 0;
        if (!revA) return 1;
        if (!revB) return -1;
        if (revA.type === "special") return -1;
        if (revB.type === "special") return 1;
        if (revA.base !== revB.base) return revA.base.localeCompare(revB.base);
        if (revA.type === "major" && revB.type !== "major") return -1;
        if (revB.type === "major" && revA.type !== "major") return 1;
        if (revA.minorNum !== revB.minorNum)
          return revA.minorNum - revB.minorNum;
        return revA.amendChar.localeCompare(revB.amendChar);
      });

      const currentRevisionIndex = sortedRevisions.findIndex(
        (rev) =>
          rev.procedure_revision_id === currentRevision.procedure_revision_id,
      );

      if (currentRevisionIndex === -1) {
        return {
          canCreate: false,
          reason: "Current revision not found in revision list",
        };
      }

      // Get revisions that come after the current revision
      const subsequentRevisions = sortedRevisions.slice(
        currentRevisionIndex + 1,
      );

      // Helper function to check if a revision is rejected
      const isRejected = (revision) => {
        const status = revision?.status_changes
          ?.find((sc) => sc.is_latest)
          ?.status_changed_to?.toLowerCase();
        return status === "rejected";
      };

      // Helper function to check if a revision is in active status
      const isActiveStatus = (revision) => {
        const status = revision?.status_changes
          ?.find((sc) => sc.is_latest)
          ?.status_changed_to?.toLowerCase();
        return [
          "not submitted",
          "cancel requested",
          "change requested",
          "approved",
          "submitted for approval",
        ].includes(status);
      };

      // Helper function to check if a revision is released or archived
      const isReleasedOrArchived = (revision) => {
        const status = revision?.status_changes
          ?.find((sc) => sc.is_latest)
          ?.status_changed_to?.toLowerCase();
        return status === "released" || status === "archived";
      };

      // Helper function to check if a revision is an amendment
      const isAmendment = (revision) => {
        const revParsed = parseRevision(revision?.rev);
        return (
          revParsed &&
          (revParsed.type === "amend" || revParsed.type === "amend_minorless")
        );
      };

      // Helper function to check if a revision is a non-rejected major/minor (not amendment)
      const isNonRejectedMajorMinor = (revision) => {
        const revParsed = parseRevision(revision?.rev);
        const status = revision?.status_changes
          ?.find((sc) => sc.is_latest)
          ?.status_changed_to?.toLowerCase();
        return (
          revParsed &&
          (revParsed.type === "major" || revParsed.type === "minor") &&
          status !== "rejected"
        );
      };

      // PRIORITY RULE: Check Released/Archived revisions FIRST (before checking revisions above)
      // This ensures Archived revisions can always create amendments
      if (isReleasedOrArchived(currentRevision)) {
        // For released/archived major or minor revisions with no amended revisions, allow amendment creation
        if (
          currentRevParsed.type === "major" ||
          currentRevParsed.type === "minor"
        ) {
          // Check if there are any amended revisions for this major/minor revision
          const hasAmendedRevisions = allRevisions.some((rev) => {
            const revParsed = parseRevision(rev.rev);
            if (!revParsed) return false;

            // Check if this is an amended revision for the same base and minor number
            if (currentRevParsed.type === "major") {
              return (
                revParsed.type === "amend_minorless" &&
                revParsed.base === currentRevParsed.base &&
                revParsed.minorNum === 0
              );
            } else if (currentRevParsed.type === "minor") {
              return (
                revParsed.type === "amend" &&
                revParsed.base === currentRevParsed.base &&
                revParsed.minorNum === currentRevParsed.minorNum
              );
            }
            return false;
          });

          if (!hasAmendedRevisions) {
            // Check if this is the last revision in the system
            const isLastRevision =
              currentRevisionIndex === sortedRevisions.length - 1;

            if (isLastRevision) {
              return {
                canCreate: true,
                reason:
                  "Can create next major, minor, or amended revision from last released/archived revision (no amendments exist)",
              };
            } else {
              // Check if all subsequent revisions are rejected
              const allSubsequentRejected =
                subsequentRevisions.length > 0 &&
                subsequentRevisions.every((rev) => {
                  const status = rev?.status_changes
                    ?.find((sc) => sc.is_latest)
                    ?.status_changed_to?.toLowerCase();
                  return status === "rejected";
                });

              if (allSubsequentRejected) {
                return {
                  canCreate: true,
                  reason:
                    "Can create next major, minor, or amended revision from released/archived revision (all subsequent are rejected)",
                };
              } else {
                // Check if there are active subsequent revisions (Not Submitted, Change Requested, etc.)
                // If yes, only allow amendments
                const hasActiveSubsequent =
                  subsequentRevisions.some(isActiveStatus);
                if (hasActiveSubsequent) {
                  return {
                    canCreate: true,
                    reason:
                      "Can create amendment only from released/archived revision",
                  };
                } else {
                  return {
                    canCreate: true,
                    reason:
                      "Can create amendment from released/archived major/minor revision (no amendments exist)",
                  };
                }
              }
            }
          }
        }

        // For released/archived revisions with all subsequent revisions rejected, allow all revision types
        const allSubsequentRejected =
          subsequentRevisions.length > 0 &&
          subsequentRevisions.every((rev) => {
            const status = rev?.status_changes
              ?.find((sc) => sc.is_latest)
              ?.status_changed_to?.toLowerCase();
            return status === "rejected";
          });

        if (allSubsequentRejected) {
          return {
            canCreate: true,
            reason:
              "Can create next major, minor, or amended revision from released/archived revision (all subsequent are rejected)",
          };
        } else {
          // Also allow if there are no subsequent revisions at all
          if (subsequentRevisions.length === 0) {
            return {
              canCreate: true,
              reason:
                "Can create next major, minor, or amended revision from released/archived revision (no subsequent revisions)",
            };
          }
        }

        // If Released/Archived and has active subsequent revisions, only allow amendments
        const hasActiveSubsequent = subsequentRevisions.some(isActiveStatus);
        if (hasActiveSubsequent) {
          return {
            canCreate: true,
            reason: "Can create amendment only from released/archived revision",
          };
        }

        // CATCH-ALL: If Released/Archived revision didn't match above conditions, allow amendments
        // This ensures Archived revisions ALWAYS return here and never fall through to "revisions above" check
        return {
          canCreate: true,
          reason: "Can create amendment from released/archived revision",
        };
      }

      // PRIORITY RULE: Check amended revisions BEFORE checking revisions above
      // This allows creating next amendment from an amendment if it's the last non-rejected in its series,
      // even if there are non-rejected revisions above (from other series)
      // For amended revisions, check special rules
      if (
        currentRevParsed &&
        (currentRevParsed.type === "amend" ||
          currentRevParsed.type === "amend_minorless")
      ) {
        const currentRevisionIndexForBelowCheck = sortedRevisions.findIndex(
          (rev) =>
            rev.procedure_revision_id === currentRevision.procedure_revision_id,
        );
        if (currentRevisionIndexForBelowCheck !== -1) {
          const revisionsBelow = sortedRevisions.slice(
            currentRevisionIndexForBelowCheck + 1,
          );

          // Check for non-rejected amendments in the SAME SERIES (same base + minor number)
          // This is the key requirement: "The next Amended Revision may only be created from
          // the last revision in the minor revision series that is not rejected"
          const sameSeriesAmendmentsBelow = revisionsBelow.filter((rev) => {
            const revParsed = parseRevision(rev.rev);
            if (!revParsed) return false;

            // Check if it's in the same series (same base and minor number)
            const isSameSeries =
              revParsed.base === currentRevParsed.base &&
              revParsed.minorNum === currentRevParsed.minorNum;

            if (!isSameSeries) return false;

            // Check if it's an amendment in the same series
            return (
              revParsed.type === "amend" || revParsed.type === "amend_minorless"
            );
          });

          // Check if there are any non-rejected amendments in the same series below
          const hasNonRejectedAmendmentInSameSeriesBelow =
            sameSeriesAmendmentsBelow.some((rev) => {
              const status = rev?.status_changes
                ?.find((sc) => sc.is_latest)
                ?.status_changed_to?.toLowerCase();
              return status !== "rejected";
            });

          // If there are non-rejected amendments in the same series below,
          // the current amendment is NOT the last non-rejected in its series
          if (hasNonRejectedAmendmentInSameSeriesBelow) {
            return {
              canCreate: false,
              reason:
                "Only the last non-rejected revision in the minor revision series can create the next amendment",
            };
          }

          // Current amendment is the last non-rejected in its series
          // Now check for any non-rejected revisions below (in any series)
          const hasNonRejectedBelow = revisionsBelow.some((rev) => {
            const status = rev?.status_changes
              ?.find((sc) => sc.is_latest)
              ?.status_changed_to?.toLowerCase();
            return status !== "rejected";
          });

          if (hasNonRejectedBelow) {
            // Current amendment is the last non-rejected in its series, but there are other non-rejected revisions below (from other series)
            // This is allowed - we can create the next amendment from this one
            return {
              canCreate: true,
              reason:
                "Can create next amendment only (last non-rejected in series)",
            };
          } else {
            // Current amendment is the last non-rejected in its series and no other non-rejected revisions below
            return {
              canCreate: true,
              reason:
                "Can create next major, minor, or amended revision from last amended revision (no non-rejected below)",
            };
          }
        }
      }

      // Check if there are any non-rejected revisions above the current revision
      // This check is skipped for amended revisions (handled above) and released/archived revisions
      const currentRevisionIndexForAboveCheck = sortedRevisions.findIndex(
        (rev) =>
          rev.procedure_revision_id === currentRevision.procedure_revision_id,
      );

      if (currentRevisionIndexForAboveCheck !== -1) {
        const revisionsAbove = sortedRevisions.slice(
          0,
          currentRevisionIndexForAboveCheck,
        );
        const hasNonRejectedAbove = revisionsAbove.some((rev) => {
          const status = rev?.status_changes
            ?.find((sc) => sc.is_latest)
            ?.status_changed_to?.toLowerCase();
          return status !== "rejected";
        });

        if (hasNonRejectedAbove) {
          // Check if this is the last non-rejected revision
          const nonRejectedRevisions = sortedRevisions.filter((rev) => {
            const status = rev?.status_changes
              ?.find((sc) => sc.is_latest)
              ?.status_changed_to?.toLowerCase();
            return status !== "rejected";
          });

          const isLastNonRejectedRevision =
            nonRejectedRevisions.length > 0 &&
            currentRevision.procedure_revision_id ===
              nonRejectedRevisions[nonRejectedRevisions.length - 1]
                .procedure_revision_id;

          if (isLastNonRejectedRevision) {
            const currentStatus = currentRevision?.status_changes
              ?.find((sc) => sc.is_latest)
              ?.status_changed_to?.toLowerCase();
            if (currentStatus !== "rejected") {
              return {
                canCreate: true,
                reason:
                  "Can create next major, minor, or amended revision from last non-rejected revision",
              };
            }
          } else {
            return {
              canCreate: false,
              reason:
                "Cannot create revision - there are non-rejected revisions above this revision",
            };
          }
        }
      }

      // Check if there's a last non-rejected revision and all revisions below it are rejected
      const nonRejectedRevisions = sortedRevisions.filter((rev) => {
        const status = rev?.status_changes
          ?.find((sc) => sc.is_latest)
          ?.status_changed_to?.toLowerCase();
        return status !== "rejected";
      });

      if (nonRejectedRevisions.length > 0) {
        let lastNonRejectedRevision =
          nonRejectedRevisions[nonRejectedRevisions.length - 1];
        const lastNonRejectedParsed = parseRevision(
          lastNonRejectedRevision.rev,
        );
        if (
          lastNonRejectedParsed &&
          (lastNonRejectedParsed.type === "amend" ||
            lastNonRejectedParsed.type === "amend_minorless")
        ) {
          for (let i = nonRejectedRevisions.length - 1; i >= 0; i--) {
            const revParsed = parseRevision(nonRejectedRevisions[i].rev);
            if (
              revParsed &&
              (revParsed.type === "major" || revParsed.type === "minor")
            ) {
              lastNonRejectedRevision = nonRejectedRevisions[i];
              break;
            }
          }
        }

        if (
          currentRevision.procedure_revision_id ===
          lastNonRejectedRevision.procedure_revision_id
        ) {
          const lastNonRejectedIndex = sortedRevisions.findIndex(
            (rev) =>
              rev.procedure_revision_id ===
              lastNonRejectedRevision.procedure_revision_id,
          );
          const revisionsBelowLastNonRejected = sortedRevisions.slice(
            lastNonRejectedIndex + 1,
          );
          const allBelowRejected =
            revisionsBelowLastNonRejected.length === 0 ||
            revisionsBelowLastNonRejected.every((rev) => {
              const status = rev?.status_changes
                ?.find((sc) => sc.is_latest)
                ?.status_changed_to?.toLowerCase();
              return status === "rejected";
            });

          if (allBelowRejected) {
            return {
              canCreate: true,
              reason:
                "Can create next major, minor, or amended revision from last non-rejected revision (all below are rejected)",
            };
          }
        }
      }

      // Walk back for amended revision creation
      const amendedRevisions = sortedRevisions.filter(isAmendment);
      if (amendedRevisions.length > 0) {
        let lastNonRejectedAmended = null;
        for (let i = amendedRevisions.length - 1; i >= 0; i--) {
          const status = amendedRevisions[i]?.status_changes
            ?.find((sc) => sc.is_latest)
            ?.status_changed_to?.toLowerCase();
          if (status !== "rejected") {
            lastNonRejectedAmended = amendedRevisions[i];
            break;
          }
        }

        let fallbackMajorMinor = null;
        if (!lastNonRejectedAmended) {
          for (let i = sortedRevisions.length - 1; i >= 0; i--) {
            if (isNonRejectedMajorMinor(sortedRevisions[i])) {
              fallbackMajorMinor = sortedRevisions[i];
              break;
            }
          }
        }

        const targetRevision = lastNonRejectedAmended || fallbackMajorMinor;

        if (
          targetRevision &&
          currentRevision.procedure_revision_id ===
            targetRevision.procedure_revision_id
        ) {
          if (
            currentRevParsed.type === "amend" ||
            currentRevParsed.type === "amend_minorless"
          ) {
            const currentAmendChar = currentRevParsed.amendChar;
            const nextAmendChar = String.fromCharCode(
              currentAmendChar.charCodeAt(0) + 1,
            );
            const nextAmendRev =
              currentRevParsed.minorNum === 0
                ? `${currentRevParsed.base}-${nextAmendChar}`
                : `${currentRevParsed.base}${currentRevParsed.minorNum}-${nextAmendChar}`;

            const existingRevision = allRevisions.find(
              (rev) => rev.rev === nextAmendRev,
            );
            if (existingRevision) {
              return {
                canCreate: false,
                reason: `Amendment ${nextAmendRev} already exists`,
              };
            }
            return {
              canCreate: true,
              reason:
                "Can create next amendment from most recent non-rejected amended or released/approved revision",
            };
          }
          return {
            canCreate: true,
            reason:
              "Can create next amendment from most recent non-rejected amended or released/approved revision",
          };
        }

        if (
          targetRevision &&
          currentRevision.procedure_revision_id !==
            targetRevision.procedure_revision_id
        ) {
          if (
            currentRevParsed.type === "amend" ||
            currentRevParsed.type === "amend_minorless"
          ) {
            return {
              canCreate: false,
              reason:
                "Only the most recent non-rejected amended or released/approved revision can create the next amendment",
            };
          }
        }
      }

      // Note: Removed the blocking rule here - Archived/Released revisions should always be able to create amendments
      // The specific amendment creation logic is handled in the rules below

      // Rule 1: For active status revisions
      if (isActiveStatus(currentRevision)) {
        if (subsequentRevisions.length === 0) {
          return { canCreate: true, reason: "No subsequent revisions exist" };
        }

        const hasNonRejectedSubsequent = subsequentRevisions.some(
          (rev) => !isRejected(rev),
        );

        if (hasNonRejectedSubsequent) {
          if (
            currentRevParsed.type === "amend" ||
            currentRevParsed.type === "amend_minorless"
          ) {
            const currentAmendChar = currentRevParsed.amendChar;
            const nextAmendChar = String.fromCharCode(
              currentAmendChar.charCodeAt(0) + 1,
            );
            const nextAmendRev =
              currentRevParsed.minorNum === 0
                ? `${currentRevParsed.base}-${nextAmendChar}`
                : `${currentRevParsed.base}${currentRevParsed.minorNum}-${nextAmendChar}`;

            const existingRevision = allRevisions.find(
              (rev) => rev.rev === nextAmendRev,
            );
            if (existingRevision) {
              return {
                canCreate: false,
                reason: `Amendment ${nextAmendRev} already exists`,
              };
            }
            return { canCreate: true, reason: "Can create amendment only" };
          } else {
            return {
              canCreate: true,
              reason: "Can create amendment only (e.g., A1a)",
            };
          }
        } else {
          return {
            canCreate: true,
            reason:
              "All subsequent revisions are rejected, can create any revision type",
          };
        }
      }

      // Rule 2: For released/archived revisions
      if (isReleasedOrArchived(currentRevision)) {
        const hasActiveSubsequent = subsequentRevisions.some(isActiveStatus);

        if (hasActiveSubsequent) {
          if (
            currentRevParsed.type === "amend" ||
            currentRevParsed.type === "amend_minorless"
          ) {
            const currentAmendChar = currentRevParsed.amendChar;
            const nextAmendChar = String.fromCharCode(
              currentAmendChar.charCodeAt(0) + 1,
            );
            const nextAmendRev =
              currentRevParsed.minorNum === 0
                ? `${currentRevParsed.base}-${nextAmendChar}`
                : `${currentRevParsed.base}${currentRevParsed.minorNum}-${nextAmendChar}`;

            const existingRevision = allRevisions.find(
              (rev) => rev.rev === nextAmendRev,
            );
            if (existingRevision) {
              return {
                canCreate: false,
                reason: `Amendment ${nextAmendRev} already exists`,
              };
            }
            return {
              canCreate: true,
              reason: "Can create next amendment only",
            };
          } else {
            return {
              canCreate: true,
              reason: "Can create amendment only (e.g., A1a)",
            };
          }
        } else {
          const allSubsequentRejected =
            subsequentRevisions.length > 0 &&
            subsequentRevisions.every(isRejected);

          if (allSubsequentRejected) {
            return {
              canCreate: true,
              reason:
                "All subsequent revisions are rejected, can create any revision type",
            };
          } else {
            return {
              canCreate: true,
              reason:
                "No subsequent revisions exist, can create any revision type",
            };
          }
        }
      }

      // Default case
      return {
        canCreate: false,
        reason: `Revision creation not allowed for status: ${currentStatus}`,
      };
    };

    const RedirectToRevision = useRedirectToRevision();

    // Get current revision data for modal
    const currentRev = revisionData?.find(
      (rev) => rev.procedure_revision_id === revisionId,
    );

    // Check if there are revisions below with "Not Submitted" or "Change Requested" status
    // This should only block Major/Minor creation, NOT Amendment creation
    const checkRevisionsBelowForWarning = (currentRevision) => {
      if (!currentRevision || !revisionData || revisionData.length === 0) {
        return false;
      }

      // Get current revision status
      const currentStatus = currentRevision?.status_changes
        ?.find((sc) => sc.is_latest)
        ?.status_changed_to?.toLowerCase();

      // If current revision is Released or Archived, allow amendment creation regardless of what's below
      // So we should NOT block in this case
      if (currentStatus === "released" || currentStatus === "archived") {
        return false;
      }

      // Parse current revision to check if it's Major or Minor
      const currentRevParsed = parseRevision(currentRevision.rev);
      if (!currentRevParsed) {
        return false;
      }

      // Only check for Major or Minor revisions
      if (
        currentRevParsed.type !== "major" &&
        currentRevParsed.type !== "minor"
      ) {
        return false;
      }

      // Sort revisions to find subsequent revisions
      const sortedRevisions = [...revisionData].sort((a, b) => {
        const revA = parseRevision(a.rev);
        const revB = parseRevision(b.rev);
        if (!revA && !revB) return 0;
        if (!revA) return 1;
        if (!revB) return -1;
        if (revA.type === "special") return -1;
        if (revB.type === "special") return 1;
        if (revA.base !== revB.base) return revA.base.localeCompare(revB.base);
        if (revA.type === "major" && revB.type !== "major") return -1;
        if (revB.type === "major" && revA.type !== "major") return 1;
        if (revA.minorNum !== revB.minorNum)
          return revA.minorNum - revB.minorNum;
        return revA.amendChar.localeCompare(revB.amendChar);
      });

      const currentRevisionIndex = sortedRevisions.findIndex(
        (rev) =>
          rev.procedure_revision_id === currentRevision.procedure_revision_id,
      );

      if (currentRevisionIndex === -1) {
        return false;
      }

      // Get revisions below the current revision
      const revisionsBelow = sortedRevisions.slice(currentRevisionIndex + 1);

      // Check if any revision below has "Not Submitted" or "Change Requested" status
      const hasWarningStatus = revisionsBelow.some((rev) => {
        const status = rev?.status_changes
          ?.find((sc) => sc.is_latest)
          ?.status_changed_to?.toLowerCase();
        return status === "not submitted" || status === "change requested";
      });

      return hasWarningStatus;
    };

    // Validate and open create revision modal
    const onCreateRevision = () => {
      // Close additional information container if open
      if (onCloseAdditionalInfo) {
        onCloseAdditionalInfo();
      }
      // If no revisions exist, allow creation
      if (revisionData.length === 0) {
        setIsCreateRevisionModalOpen(true);
        return;
      }

      // Get the current revision being viewed
      const currentRevision = revisionData.find(
        (rev) => rev.procedure_revision_id === revisionId,
      );

      if (!currentRevision) {
        showSnackbar("Current revision not found!", "error");
        return;
      }

      // Use the comprehensive canCreateRevision function
      const { canCreate, reason } = canCreateRevision(
        currentRevision,
        revisionData,
      );

      if (!canCreate) {
        showSnackbar(reason, "error");
        return;
      }

      // Check for warning condition: creating from Major/Minor with revisions below in "Not Submitted" or "Change Requested" status
      const hasWarning = checkRevisionsBelowForWarning(currentRevision);
      if (hasWarning) {
        showSnackbar(
          "Cannot create revision - there are revisions below with 'Not Submitted' or 'Change Requested' status",
          "error",
        );
        return;
      }

      // All validations passed, open the modal
      setIsCreateRevisionModalOpen(true);
    };

    // Handle successful revision creation
    const handleRevisionCreated = (createdRevisionId) => {
      setIsCreateRevisionModalOpen(false);

      // Navigate directly to the newly created revision
      // New revisions are always in "Not Submitted" status
      if (createdRevisionId && partId) {
        const redirectUrl = `/revision-submission/${partId}/${myRevision}/${type}/${partNumber}/${createdRevisionId}/no-description`;
        navigate(redirectUrl, { state: { status: "Not Submitted" } });
      }

      // Call the onRevisionCreated callback if provided (for data refresh)
      if (onRevisionCreated) {
        onRevisionCreated(createdRevisionId);
      }
    };
    return (
      <>
        {showHeading && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3>Revisions Table</h3>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {headingRightContent}
              {showCreateButton && (
                <div className="button-container" style={{ margin: 0 }}>
                  <button
                    className="create-revision-button"
                    onClick={() => {
                      if (!isCreateButtonDisabled) {
                        onCreateRevision();
                      }
                    }}
                    disabled={isCreateButtonDisabled}
                    style={{
                      opacity: isCreateButtonDisabled ? 0.5 : 1,
                      cursor: isCreateButtonDisabled
                        ? "not-allowed"
                        : "pointer",
                    }}
                    title={
                      isCreateButtonDisabled
                        ? !canCreate
                          ? "You do not have permission to create revisions"
                          : "Revision is locked by another user"
                        : undefined
                    }
                  >
                    Create New Revision
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <table
          className={`table ${hideLinkStyle ? "revision-table-hide-link-style" : ""}`}
          style={{
            marginTop:
              tableMarginTop ?? (showHeading ? "-10px" : "14px"),
            marginBottom: tableMarginBottom ?? undefined,
          }}
        >
          <thead>
            <tr>
              <th>Rev</th>
              <th>Date Released</th>
              <th>Revision Details</th>
              <th>Submitter / Requestor</th>
              <th>Approver</th>
              <th>References</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {[...revisionData].sort(revisionSort).map((rev, index) => {
              // Flatten all required_by entries from status_changes
              const requiredBy = "required_by" in rev ? rev.required_by : [];

              // Find users by purpose
              const submitter = requiredBy.find(
                (rb) => rb.purpose?.toLowerCase() === "submit",
              )?.user;

              const approvers = requiredBy
                .filter((rb) => rb.purpose?.toLowerCase() === "approve")
                .map((rb) => rb.user)
                .filter(Boolean); // filter out nulls

              const isCurrentOpnRev = rev.procedure_revision_id === revisionId;
              const showNavLink =
                !disableRowNavigation &&
                !isReadOnly &&
                !rev.is_maintained_outside_system &&
                !isCurrentOpnRev;
              const revisionCellStyle =
                showNavLink && !hideLinkStyle
                  ? { cursor: "pointer", textDecoration: "underline" }
                  : hideLinkStyle || disableRowNavigation
                    ? {
                        color: "#212529",
                        cursor: "default",
                        textDecoration: "none",
                      }
                    : {};

              let currentStatus = getCurrentStatus(rev);
              return (
                <tr
                  key={index}
                  className={`${isCurrentOpnRev && "hilight-current-revision"}`}
                >
                  <td
                    style={revisionCellStyle}
                    onClick={() => {
                      if (!showNavLink) return;
                      RedirectToRevision({
                        type_name: rev.type_name,
                        part_number: rev.part_number,
                        procedureRevisionId: rev.procedure_revision_id,
                        description: rev.part_description,
                        status: currentStatus,
                        partId: rev.part_ref,
                        revision: rev.rev,
                      });
                    }}
                  >
                    {rev.rev || "-"}
                  </td>
                  <td>
                    {rev.date_released
                      ? new Date(rev.date_released).toLocaleDateString(
                          "ja-JP",
                          {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          },
                        )
                      : "-"}
                  </td>
                  <td>{rev.revision_details || "-"}</td>
                  <td>
                    {rev.status_changes?.find((sc) => sc.is_latest)
                      ?.status_changed_to === "MAINTAINED OUTSIDE OF SYSTEM" ? (
                      <div>MAINTAINED OUTSIDE OF SYSTEM</div>
                    ) : (
                      requiredBy
                        .filter((rb) => rb.purpose?.toLowerCase() === "submit")
                        .map((rb, i) => {
                          const name = formatName(rev, rb.user);
                          return (
                            <div key={i}>
                              {!rb.is_open
                                ? `Submitted by ${name}`
                                : `Pending Submission from ${name}`}
                            </div>
                          );
                        })
                    )}
                  </td>

                  <td>
                    {rev.status_changes?.find((sc) => sc.is_latest)
                      ?.status_changed_to === "MAINTAINED OUTSIDE OF SYSTEM" ? (
                      <div>MAINTAINED OUTSIDE OF SYSTEM</div>
                    ) : (
                      requiredBy
                        .filter((rb) => rb.purpose?.toLowerCase() === "approve")
                        .map((rb, i) => {
                          const name = formatName(rev, rb.user);
                          return (
                            <div key={i}>
                              {!rb.is_open
                                ? `Approved by ${name}`
                                : `Pending Approval from ${name}`}
                            </div>
                          );
                        })
                    )}
                  </td>

                  <td>{rev.reference || "-"}</td>
                  <td>{currentStatus}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Create Revision Modal */}
        <CreateNewRevisionModal
          isOpen={isCreateRevisionModalOpen}
          onClose={() => setIsCreateRevisionModalOpen(false)}
          mode={MODAL_MODES.CREATE}
          RevisionSelectorComponent={RevisionSelection}
          partId={partId}
          partNumber={partNumber}
          type={type}
          description={description}
          myRevision={myRevision}
          currentRev={currentRev}
          revisionData={revisionData}
          users={users}
          approvedUsers={approvedUsers}
          releasedUsers={releasedUsers}
          rule={rule}
          onSuccess={handleRevisionCreated}
        />

        {/* Snackbar for validation errors */}
        {snackbar.open && (
          <Snackbar
            message={snackbar.message}
            type={snackbar.type}
            onClose={handleCloseSnackbar}
          />
        )}
      </>
    );
  },
);

RevisionTable.displayName = "RevisionTable";

export default RevisionTable;
